import React, { useEffect, useState } from 'react';
import PropType from 'prop-types';
import {
  Button,
  CardBody,
  Typography,
  TileGroup, Tile, TileContent, TileText
} from '@one-thd/sui-atomic-components';
import isEmpty from 'lodash/isEmpty';
import StoreLocalizationModal from '../StoreLocalizationModal';
import { useIsMobile, responsiveStyles } from './helpers';
import { storeType } from './types';
import ZipCodeInput from '../inputZipcode';
import { analyticConstants } from '../../utils/constants';

const EditStoreLocation = ({
  fetchStores,
  localZipCode,
  setLocalZipCode,
  setShowEditStoreLocation,
  storeApiInfo,
  storeNumber,
  trackAnalytics,
  updateStore,
  zipcode
}) => {
  const [showStoreLocalizationModal, setShowStoreLocalizationModal] = useState(false);
  const [responseStyles, setResponseStyles] = useState({});

  const isMobile = useIsMobile();

  useEffect(() => setResponseStyles(responsiveStyles(isMobile)), [isMobile]);

  const { fetchStores: fetchStore, storeClick, viewMoreClick } = analyticConstants;
  const handleUpdateStores = async (zip) => {
    trackAnalytics(fetchStore, storeClick);
    fetchStores(zip);
  };

  const handleViewMoreStores = async () => {
    trackAnalytics(fetchStore, viewMoreClick);
    setShowStoreLocalizationModal(true);
  };

  return (
    <>
      <CardBody>
        <Typography paragraph>
          Enter your ZIP Code. Then, select your store for carpet availability.
        </Typography>
        <ZipCodeInput
          zipcode={localZipCode}
          onZipcodeChange={setLocalZipCode}
          onUpdateCallback={handleUpdateStores}
        />
      </CardBody>
      <Typography variant="h4">Local Stores</Typography>
      {storeApiInfo.recordCount === 0
        ? <Typography paragraph><b>Search Results:&nbsp;</b>No stores found.</Typography>
        : (
          <>
            <TileGroup>
              {!isEmpty(storeApiInfo.stores) ? (
                (storeApiInfo.stores).slice(0, responseStyles.numTilesToShow).map((store) => (
                  <Tile
                    key={store.storeId}
                    value={store.storeId}
                    selected={storeApiInfo?.store?.storeId === store.storeId}
                    style={{ width: responseStyles.tileWidth, padding: '1.6rem', backgroundColor: 'initial' }}
                    onClick={() => {
                      updateStore(undefined, store.storeId, 'StoreLocation');
                      setShowEditStoreLocation(false);
                    }}
                  >
                    <TileContent
                      orientation="vertical"
                      alignItems="start"
                      style={{ width: '100%' }}
                    >
                      <TileText variant="h4">{`${store.name} #${store.storeId}`}</TileText>
                      <TileText color="subtle">{`${store.address.street}`}</TileText>
                      <TileText color="subtle">
                        {`${store.address.city}, ${store.address.state} ${store.address.postalCode}`}
                      </TileText>
                    </TileContent>
                  </Tile>
                ))
              ) : []}
            </TileGroup>
            <Button
              variant="text"
              style={{ border: 'none' }}
              onClick={handleViewMoreStores}
            >
              <Typography paragraph decoration="underline">View More Stores</Typography>
            </Button>
          </>
        )}
      {
        showStoreLocalizationModal && (
          <StoreLocalizationModal
            handleUpdateStore={updateStore}
            onCloseModal={() => setShowStoreLocalizationModal(false)}
            setLocalZipCode={setLocalZipCode}
            setShowEditStoreLocation={setShowEditStoreLocation}
            showModal={showStoreLocalizationModal}
            stores={storeApiInfo.stores}
            zipcode={localZipCode}
            isMobile={isMobile}
            trackAnalytics={trackAnalytics}
          />
        )
      }
    </>
  );
};

EditStoreLocation.propTypes = {
  fetchStores: PropType.func.isRequired,
  localZipCode: PropType.string.isRequired,
  setLocalZipCode: PropType.func.isRequired,
  setShowEditStoreLocation: PropType.func.isRequired,
  storeApiInfo: PropType.shape({
    recordCount: PropType.number,
    store: storeType,
    stores: PropType.arrayOf(storeType)
  }),
  storeNumber: PropType.oneOfType([PropType.number, PropType.string]),
  trackAnalytics: PropType.func.isRequired,
  updateStore: PropType.func.isRequired,
  zipcode: PropType.string,
};

EditStoreLocation.defaultProps = {
  storeApiInfo: {},
  storeNumber: '',
  zipcode: '',
};

export default EditStoreLocation;